import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";

export interface CodingDashboardCounts {
  totalClassified: number;
  totalReviewed: number;
  totalApproved: number;
  totalRejected: number;
  awaitingAdmin: number;
}

interface UseCodingDashboardCountsOptions {
  customerIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export function useCodingDashboardCounts(options?: UseCodingDashboardCountsOptions) {
  const { customerIds, dateFrom, dateTo } = options || {};

  const { data, isLoading, error, refetch } = useQuery<CodingDashboardCounts>({
    queryKey: ["coding-dashboard-counts", customerIds?.join(","), dateFrom, dateTo],
    queryFn: async () => {
      // Fetch reviewed totals via RPC function
      // The function counts unique encounters with completed reviews, filtered by observation datetime
      const reviewTotalsRes = await supabase.rpc("get_encounter_review_totals", {
        observed_from: dateFrom,
        observed_to: dateTo,
        customer_ids: customerIds?.length ? customerIds : undefined,
      });

      if (reviewTotalsRes.error) throw reviewTotalsRes.error;

      type ReviewTotals = Database["public"]["Functions"]["get_encounter_review_totals"]["Returns"][number];
      const totals: ReviewTotals = reviewTotalsRes.data?.[0] ?? {
        approved_count: 0,
        rejected_count: 0,
        total_count: 0,
      };

      // Fetch classified total and awaiting admin counts in parallel
      let classifiedQuery = supabase
        .from("encounters_with_successful_classification_run")
        .select("*", { count: "exact", head: true });

      if (customerIds?.length) {
        classifiedQuery = classifiedQuery.in("customer_id", customerIds);
      }
      if (dateFrom) {
        classifiedQuery = classifiedQuery.gte("admit_datetime", dateFrom);
      }
      if (dateTo) {
        classifiedQuery = classifiedQuery.lte("admit_datetime", dateTo);
      }

      let awaitingAdminQuery = supabase
        .from("encounter_results_approved_unwritten")
        .select("*", { count: "exact", head: true });

      if (customerIds?.length) {
        awaitingAdminQuery = awaitingAdminQuery.in("customer_id", customerIds);
      }
      if (dateFrom) {
        awaitingAdminQuery = awaitingAdminQuery.gte("observation_datetime", dateFrom);
      }
      if (dateTo) {
        awaitingAdminQuery = awaitingAdminQuery.lte("observation_datetime", dateTo);
      }

      const [classifiedRes, awaitingAdminRes] = await Promise.all([classifiedQuery, awaitingAdminQuery]);

      if (classifiedRes.error) throw classifiedRes.error;
      if (awaitingAdminRes.error) throw awaitingAdminRes.error;

      return {
        totalClassified: classifiedRes.count ?? 0,
        totalReviewed: totals.total_count,
        totalApproved: totals.approved_count,
        totalRejected: totals.rejected_count,
        awaitingAdmin: awaitingAdminRes.count ?? 0,
      } satisfies CodingDashboardCounts;
    },
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  return {
    counts: data,
    isLoading,
    error,
    refetch,
  };
}
